import { PlusIcon } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { useSafeNavigate } from '@/hooks/useSafeRouter';

/**
 * NewChatButton — "new conversation", top-right of the home header.
 *
 * Monochrome, and built from `CircleIconButton size="header"` — the same token
 * the hamburger and back-arrow use. It was previously hand-rolled: a
 * `btn-accent-gradient` circle at a bespoke `size-9` with a `size-[19px]` glyph.
 * That was three problems at once — it read as a hard dark blob against the
 * warm header, it drifted from every other header action (the exact drift
 * `size="header"` exists to prevent), and `size-[19px]` was an arbitrary value.
 *
 * Colour has to be earned. A new-chat button is a routine navigation action,
 * not a state or a primary CTA, so it stays neutral like its neighbours.
 */
export function NewChatButton({ className }: { className?: string }) {
  const navigate = useSafeNavigate();
  const location = useLocation();

  const handleClick = (): void => {
    if (location.pathname !== '/') navigate('/');
    window.dispatchEvent(new CustomEvent('createPage:newChat'));
  };

  return (
    <CircleIconButton
      ariaLabel="New chat"
      className={`header-nav-btn rounded-full ${className ?? ''}`}
      onClick={handleClick}
      size="header"
      tooltip="New chat"
      tooltipSide="bottom"
      type="button"
    >
      <PlusIcon weight="regular" />
    </CircleIconButton>
  );
}
